import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  FileText, Download, Eye, BookOpen, List,
  Calendar, HardDrive, RefreshCw, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Category icons mapping
const CATEGORY_ICONS = {
  Operations: BookOpen,
  Reference: List
};

// Category colors
const CATEGORY_COLORS = {
  Operations: 'bg-blue-100 text-blue-700',
  Reference: 'bg-emerald-100 text-emerald-700'
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SOPDocuments = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/sop/documents');
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load SOP documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDownloadPDF = async (docId, title) => {
    try {
      setDownloading(docId);
      
      const response = await api.get(`/sop/documents/${docId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `eHRMS_${docId.toUpperCase()}_SOP_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${title}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async (doc) => {
    try {
      setPreviewDoc(doc);
      setPreviewLoading(true);
      
      const response = await api.get(`/sop/documents/${doc.id}/html`, {
        responseType: 'text'
      });
      
      setPreviewContent(response.data);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Failed to load preview');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openInNewTab = (docId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${baseUrl}/api/sop/documents/${docId}/html`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                Standard Operating Procedures
              </h1>
              <p className="text-slate-600 mt-1">
                Download official SOP documents for Enterprise eHRMS operations
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchDocuments}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Document Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Operations Guide</p>
                <p className="text-lg font-semibold text-slate-900">Functional SOP</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <List className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Quick Reference</p>
                <p className="text-lg font-semibold text-slate-900">Appendix</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid gap-6">
            {documents.map((doc) => {
              const CategoryIcon = CATEGORY_ICONS[doc.category] || FileText;
              const categoryColor = CATEGORY_COLORS[doc.category] || 'bg-slate-100 text-slate-700';
              
              return (
                <Card key={doc.id} className="hover:shadow-lg transition-shadow" data-testid={`sop-doc-${doc.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${categoryColor.replace('text-', 'bg-').replace('700', '100')}`}>
                          <CategoryIcon className={`w-6 h-6 ${categoryColor.split(' ')[1]}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold text-slate-900">
                              {doc.title}
                            </h3>
                            <Badge className={categoryColor}>
                              {doc.category}
                            </Badge>
                            {!doc.available && (
                              <Badge variant="destructive">Unavailable</Badge>
                            )}
                          </div>
                          <p className="text-slate-600 mb-3">
                            {doc.description}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <HardDrive className="w-4 h-4" />
                              {formatFileSize(doc.file_size)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Last updated: {formatDate(doc.modified_date)}
                            </span>
                            <span className="text-slate-400">
                              {doc.filename}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(doc)}
                          disabled={!doc.available}
                          data-testid={`preview-${doc.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openInNewTab(doc.id)}
                          disabled={!doc.available}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open
                        </Button>
                        <Button
                          onClick={() => handleDownloadPDF(doc.id, doc.title)}
                          disabled={downloading === doc.id || !doc.available}
                          data-testid={`download-${doc.id}`}
                        >
                          {downloading === doc.id ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={previewDoc !== null} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {previewDoc?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto bg-white rounded-lg border">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <iframe
                  srcDoc={previewContent}
                  title="SOP Preview"
                  className="w-full h-full min-h-[600px] border-0"
                  sandbox="allow-same-origin"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setPreviewDoc(null)}>
                Close
              </Button>
              {previewDoc && (
                <Button onClick={() => handleDownloadPDF(previewDoc.id, previewDoc.title)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SOPDocuments;
